import { getAbsoluteUrl } from "$utils/urls.js";
import { active, csv } from "d3";
import { setContext, getContext } from "svelte";
import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
import { content_box_sentences, metadata } from "./consts.js";

export class ToolData {
  wideData = $state();
  //categoryData = $derived.by(() => this.condenseCategoryData());

  activeCategory = $state("All");
  chartData = $derived.by(() => this.getChartData());
  seriesColors = $derived.by(() => this.getChartColors());
  seriesNames = $derived.by(() => this.getChartProgramNames());
  seriesVariables = $derived.by(() => this.getChartProgramVariables());
  dropdownData = $derived.by(() => this.getDropdownData());

  hoveredProgramVariable = $state(null);
  selectedProgramVariable = $state(null);
  singledOutProgramVariable = $state(null);

  contentBoxProgramVariable = $derived(this.hoveredProgramVariable ?? this.selectedProgramVariable);
  contentBoxProgramLabel = $derived.by(() => this.getContentBoxProgramLabel());
  contentBoxText = $derived.by(() => this.getContentBoxText());

  async fetchData() {
    //these wide data are in millions already -- bring to raw numbers for formatting later
    this.wideData = await csv(getAbsoluteUrl("/program_year_data.csv"), (d) => {
      return {
        year: +d.Year,
        hhs_ced_and_rcd: +d["HHS CED + RCD"] * 1000000,
        epa_brownfields: +d["EPA Brownfields"] * 1000000,
        cdfi: +d["CDFI"] * 1000000,
        choice_neighborhoods: +d["Choice Neighborhoods"] * 1000000,
        home: +d["HOME"] * 1000000,
        eda: +d["EDA"] * 1000000,
        cdbg: +d["CDBG"] * 1000000,
        usda_rd: +d["USDA RD"] * 1000000,
        htc: +d["HTC"] * 1000000,
        nmtc: +d["NMTC"] * 1000000,
        oz: +d["OZ"] * 1000000,
        lihtc: +d["LIHTC"] * 1000000,
        promise_neighborhoods: +d["Promise Neighborhoods"] * 1000000
      };
    });
  }

  isReady() {
    let isReady = this.wideData && this.wideData.length > 0;
    return isReady || false;
  }

  getWideData() {
    return this.isReady() ? this.wideData : [];
  }

  //this function was crafted by AI and reviewed by human
  // condenseCategoryData() {
  //   if (!Array.isArray(this.wideData) || this.wideData.length === 0) return [];

  //   const categories = ["Direct spending", "Tax expenditures"];

  //   const variablesByCategory = Object.fromEntries(
  //     categories.map((category) => [
  //       category,
  //       metadata.filter((item) => item.category === category).map((item) => item.variable)
  //     ])
  //   );

  //   const sumCategoryForYear = (yearRow, category) => {
  //     return variablesByCategory[category].reduce((sum, variable) => {
  //       return sum + (yearRow[variable] ?? 0);
  //     }, 0);
  //   };

  //   return this.wideData.map((yearRow) => ({
  //     year: yearRow.year,
  //     direct_spending: sumCategoryForYear(yearRow, "Direct spending"),
  //     tax_expenditures: sumCategoryForYear(yearRow, "Tax expenditures")
  //   }));
  // }
  getChartProgramVariables() {
    if (!this.isReady()) return [];
    //if (this.activeCategory == "All") return ["direct_spending", "tax_expenditures"];
    let categoryVariables = [];
    if (this.singledOutProgramVariable) {
      categoryVariables = [this.singledOutProgramVariable];
    } else {
      categoryVariables = metadata
        .filter((d) => this.activeCategory == "All" || d.category === this.activeCategory)
        .map((d) => d.variable);
    }
    return categoryVariables;
  }
  getChartData() {
    if (!this.isReady()) return [];
    //if (this.activeCategory == "All") return this.categoryData;
    return this.wideData.map((d) => {
      let filteredData = { year: d.year };
      this.getChartProgramVariables().forEach((variable) => {
        filteredData[variable] = d[variable];
      });
      return filteredData;
    });
  }
  getChartColors() {
    if (!this.isReady()) return [];
    //if (this.activeCategory == "All") return [urbanColors.yellow, urbanColors.blue];
    return metadata
      .filter((d) => this.getChartProgramVariables().includes(d.variable))
      .map((d) => d.color);
  }
  getChartProgramNames() {
    if (!this.isReady()) return [];
    //if (this.activeCategory == "All") return ["Direct spending", "Tax expenditures"];
    return metadata
      .filter((d) => this.getChartProgramVariables().includes(d.variable))
      .map((d) => d.label);
  }

  getDropdownData() {
    if (!this.isReady()) return [];
    // if (this.activeCategory == "All")
    //   return [
    //     { label: "Direct spending", value: "direct_spending" },
    //     { label: "Tax expenditures", value: "tax_expenditures" }
    //   ];
    return metadata
      .filter((d) => this.activeCategory == "All" || d.category === this.activeCategory)
      .map((d) => ({ label: d.label, value: d.variable }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  getProgramNameFromVariable(variable) {
    if (!this.isReady()) return "";
    // if (variable == "direct_spending") {
    //   return "Direct spending";
    // }
    // if (variable == "tax_expenditures") {
    //   return "Tax expenditures";
    // }
    if (variable != null) {
      let program = metadata.find((d) => d.variable === variable);
      return program ? program.label : "";
    }
    return "";
  }

  getContentBoxProgramLabel() {
    if (!this.isReady()) return "";
    // if (this.contentBoxProgramVariable == "direct_spending") {
    //   return "Direct spending";
    // }
    // if (this.contentBoxProgramVariable == "tax_expenditures") {
    //   return "Tax expenditures";
    // }
    if (this.contentBoxProgramVariable != null) {
      let program = metadata.find((d) => d.variable === this.contentBoxProgramVariable);
      return program ? program.label : "";
    }
    if (this.activeCategory == "Direct spending") {
      return "Direct spending";
    }
    if (this.activeCategory == "Tax expenditures") {
      return "Tax expenditures";
    }
    return "Federal place-based spending";
  }

  getContentBoxText() {
    if (this.activeCategory == "All" && this.contentBoxProgramVariable == null) {
      return content_box_sentences.overview;
    }
    // if (this.activeCategory == "All") {
    //   return this.contentBoxProgramVariable == "direct_spending"
    //     ? content_box_sentences.direct
    //     : content_box_sentences.tax;
    // }
    if (this.activeCategory == "Direct spending" && this.contentBoxProgramVariable == null) {
      return content_box_sentences.direct;
    }
    if (this.activeCategory == "Tax expenditures" && this.contentBoxProgramVariable == null) {
      return content_box_sentences.tax;
    }
    let pieces_of_text = content_box_sentences.category_highlighted.split("[XX]");
    let selectedProgramMeta = metadata.find((d) => d.variable == this.contentBoxProgramVariable);
    return (
      pieces_of_text[0] +
      selectedProgramMeta?.now_number +
      pieces_of_text[1] +
      selectedProgramMeta?.short_label +
      pieces_of_text[2] +
      selectedProgramMeta?.percent_inflation +
      (this.contentBoxProgramVariable == "oz" ? selectedProgramMeta?.extra_text : pieces_of_text[3])
    );
  }
}

let contextKey = "TOOL_DATA";

export function createData() {
  const data = new ToolData();
  setContext(contextKey, data);
  return data;
}

export function useData() {
  return getContext(contextKey);
}
